// A URL that answers with exactly what you told it to, and a log of who called it.
//
// WHAT THIS IS FOR. An integration almost always has to be pointed at a URL
// before the real one exists — a switch fetching the agent list it should ring,
// a partner posting a status callback. Until now that meant standing up a
// service, or pasting the response into a public site and putting a customer's
// payloads on somebody else's infrastructure.
//
// THREE THINGS DECIDE THE SHAPE OF THIS SCREEN.
//
// 1. THE URL IS THE PRODUCT. Everything else is configuration for it. So it is
//    the most prominent thing on every row, copyable in one click, and shown
//    with a curl that works as pasted — a stub you cannot quickly call is a
//    stub you cannot quickly test.
//
// 2. THE INSPECTOR IS HALF THE VALUE. "It returns what I asked" is rarely the
//    question; "what did the switch actually send me?" is. The hits live beside
//    the response rather than on another screen, because the two are read
//    together or not at all.
//
// 3. NOT EVERY CONTENT TYPE IS OFFERED, and the reason is said out loud. This
//    serves bytes a customer supplied from a cpass domain, so HTML and anything
//    script-bearing are refused by the backend. A screen that simply omitted
//    them would read as an oversight and generate a support ticket a week.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Webhook, Plus, Loader2, Copy, Check, Trash2, RefreshCw, Inbox, X,
  AlertTriangle, Power, KeyRound, ChevronDown, ChevronRight, Clock, Moon,
} from 'lucide-react';
import {
  mockEndpointsService, MockEndpoint, MockEndpointHit, MockEndpointLimits,
  MockEndpointWindow, MockEndpointWindowStatus,
} from 'api/services/mockEndpoints.service';
import {
  byteLength, bodyProblem, formatBody, statusTone, curlFor, parseHeaders, humanBytes, isJsonType,
} from './webhookStubRules';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white '
  + 'rounded-xl text-sm';
const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const card =
  'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl';

/** The response someone building a switch integration most likely wants first. */
const STARTER_BODY = JSON.stringify({
  code: 200,
  loop: 2,
  followme: false,
  hangup: false,
  timeout: 20,
  welcome_music: '1609133greeting_music.wav',
  waiting_music: '16098620waiting_music.wav',
  result: [
    { agent_id: '101_1111112', phone: '91960000XXXX' },
    { agent_id: '102_1111112', phone: '91910000XXXX' },
  ],
}, null, 2);

const FALLBACK_LIMITS: MockEndpointLimits = {
  allowed_content_types: ['application/json', 'text/plain'],
  max_endpoints_per_org: 25,
  max_body_bytes: 65536,
  max_delay_ms: 5000,
  rate_limit_per_minute: 120,
  keep_requests_per_endpoint: 100,
  max_captured_body_bytes: 16384,
};

/** Copies text and says so for a moment, because a silent copy reads as broken. */
const CopyButton: React.FC<{ text: string; label?: string; className?: string }> =
({ text, label, className }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          // Clipboard is refused without a user gesture or over plain http.
          // The text is on screen and selectable, so this is a missing
          // convenience, not a missing capability — nothing to report.
        }
      }}
      className={className
        || 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border '
           + 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
    >
      {done ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      {label ?? (done ? 'Copied' : 'Copy')}
    </button>
  );
};

const StatusBadge: React.FC<{ status: number }> = ({ status }) => {
  const tone = statusTone(status);
  const cls = tone === 'ok'
    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
    : tone === 'warn'
      ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
      : 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300';
  return <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${cls}`}>{status}</span>;
};

const WebhookStubs: React.FC = () => {
  const [rows, setRows] = useState<MockEndpoint[]>([]);
  const [limits, setLimits] = useState<MockEndpointLimits>(FALLBACK_LIMITS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<MockEndpoint> | null>(null);
  const [inspecting, setInspecting] = useState<MockEndpoint | null>(null);
  const [scheduling, setScheduling] = useState<MockEndpoint | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, lim] = await Promise.all([
      mockEndpointsService.list(),
      mockEndpointsService.limits(),
    ]);
    setLoading(false);
    if (list.success) { setRows(list.data || []); setError(null); }
    else setError(list.error || 'Could not load your stub endpoints');
    // Not fatal: the form falls back to conservative limits, which are the
    // real ones unless the backend has been reconfigured.
    if (lim.success && lim.data) setLimits(lim.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const atLimit = rows.length >= limits.max_endpoints_per_org;

  const remove = async (row: MockEndpoint) => {
    // Deleting takes the URL out of service for whatever is still calling it,
    // and the request log with it. Both are named, because "Delete?" does not
    // tell somebody what they are about to break.
    const ok = window.confirm(
      `Delete "${row.name}"?\n\n`
      + 'The URL stops answering immediately — anything still calling it will start failing — '
      + `and its ${(row.hit_count || 0).toLocaleString()} recorded hits are deleted too. `
      + 'This cannot be undone.',
    );
    if (!ok) return;
    const res = await mockEndpointsService.remove(row.id as number);
    if (res.success) load();
    else setError(res.error || 'Could not delete that stub endpoint');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <Webhook className="w-7 h-7 text-indigo-600 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Webhook stubs</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            A URL that answers with exactly the response you define — and records what callers sent it.
            Point an integration at one before the real endpoint exists.
          </p>
        </div>
        <button
          type="button"
          disabled={atLimit}
          onClick={() => setEditing({
            name: '', status_code: 200, content_type: 'application/json',
            body: STARTER_BODY, delay_ms: 0, enabled: true, capture_requests: true,
          })}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-indigo-600 text-white
                     hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          title={atLimit ? `You already have ${limits.max_endpoints_per_org}, which is the limit` : undefined}
        >
          <Plus className="w-4 h-4" /> New stub
        </button>
      </div>

      {error && (
        <div className="rounded-xl px-3 py-2 text-sm bg-rose-50 text-rose-800
                        dark:bg-rose-900/20 dark:text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {atLimit && (
        <div className="rounded-xl px-3 py-2 text-xs bg-amber-50 text-amber-900
                        dark:bg-amber-900/20 dark:text-amber-200">
          You have {rows.length} stubs, which is the limit. Delete one that is no longer being called
          to make room for another.
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm py-10 justify-center"
             style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className={`${card} p-8 text-center`}>
          <Webhook className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No stubs yet</p>
          <p className="text-xs mt-1 max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
            Create one, paste in the response you need, and you get a URL that returns it. Whatever
            calls that URL is recorded, so you can see exactly what it sent.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <StubRow
              key={row.id}
              row={row}
              onEdit={() => setEditing(row)}
              onInspect={() => setInspecting(row)}
              onSchedule={() => setScheduling(row)}
              onDelete={() => remove(row)}
              onChanged={load}
              onError={setError}
            />
          ))}
        </div>
      )}

      {editing && (
        <StubEditor
          initial={editing}
          limits={limits}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {inspecting && (
        <HitInspector
          endpoint={inspecting}
          limits={limits}
          onClose={() => setInspecting(null)}
        />
      )}

      {scheduling && (
        <WindowEditor
          endpoint={scheduling}
          limits={limits}
          onClose={() => setScheduling(null)}
        />
      )}
    </div>
  );
};

// ── one stub in the list ──────────────────────────────────────────────────────

const StubRow: React.FC<{
  row: MockEndpoint;
  onEdit: () => void;
  onInspect: () => void;
  onSchedule: () => void;
  onDelete: () => void;
  onChanged: () => void;
  onError: (m: string) => void;
}> = ({ row, onEdit, onInspect, onSchedule, onDelete, onChanged, onError }) => {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    const res = await mockEndpointsService.update(row.id as number, { enabled: !row.enabled });
    setBusy(false);
    if (res.success) onChanged();
    else onError(res.error || 'Could not change that');
  };

  const rotate = async () => {
    const ok = window.confirm(
      'Issue a new URL for this stub?\n\n'
      + 'The current URL stops working immediately, so anything already pointed at it must be '
      + 'updated. The response and the recorded hits are kept.',
    );
    if (!ok) return;
    setBusy(true);
    const res = await mockEndpointsService.rotateToken(row.id as number);
    setBusy(false);
    if (res.success) onChanged();
    else onError(res.error || 'Could not issue a new URL');
  };

  return (
    <div className={`${card} p-4`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white truncate">{row.name}</span>
            <StatusBadge status={row.status_code || 200} />
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700
                             text-gray-600 dark:text-gray-300">
              {row.content_type}
            </span>
            {!row.enabled && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700
                               text-gray-500 dark:text-gray-400">off</span>
            )}
            {row.expired && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800
                               dark:bg-amber-900/20 dark:text-amber-200">expired</span>
            )}
          </div>
          {row.description && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{row.description}</p>
          )}

          {/* The URL is the product — largest, first, and copyable in one click. */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {row.url ? (
              <>
                <code className="text-xs font-mono px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-900
                                 border border-gray-200 dark:border-gray-700 break-all">
                  {row.url}
                </code>
                <CopyButton text={row.url} label="Copy URL" />
              </>
            ) : (
              // cpass.public-base-url is unset, so cpass does not know its own
              // address. Saying that is more use than rendering "null/v1/mock/…".
              <span className="text-xs text-amber-700 dark:text-amber-300">
                This deployment has no public base URL configured, so the full address cannot be
                shown. The path is <code className="font-mono">/v1/mock/{row.token}</code>.
              </span>
            )}
          </div>

          <div className="mt-2 text-[11px] flex items-center gap-3 flex-wrap"
               style={{ color: 'var(--text-muted)' }}>
            <span>{(row.hit_count || 0).toLocaleString()} hits</span>
            {row.last_hit_at && <span>last {new Date(row.last_hit_at).toLocaleString()}</span>}
            {!!row.delay_ms && <span>{row.delay_ms} ms delay</span>}
            {!row.capture_requests && <span>not recording callers</span>}
            {row.expires_at && !row.expired && (
              <span>expires {new Date(row.expires_at).toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button type="button" onClick={onInspect}
            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border
                       border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            <Inbox className="w-3.5 h-3.5" /> Requests
          </button>
          <button type="button" onClick={onSchedule}
            className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border
                       border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            <Clock className="w-3.5 h-3.5" /> Time windows
          </button>
          <button type="button" onClick={onEdit}
            className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                       hover:bg-gray-50 dark:hover:bg-gray-700">
            Edit
          </button>
          <button type="button" onClick={toggle} disabled={busy} title={row.enabled ? 'Turn off' : 'Turn on'}
            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                       hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            <Power className={`w-3.5 h-3.5 ${row.enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
          </button>
          <button type="button" onClick={rotate} disabled={busy} title="Issue a new URL"
            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                       hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            <KeyRound className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onDelete} title="Delete"
            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                       hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {row.url && (
        <details className="mt-3">
          <summary className="text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
            Call it
          </summary>
          <div className="mt-2 flex items-start gap-2">
            <pre className="flex-1 text-[11px] font-mono p-2 rounded-lg overflow-x-auto
                            bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
{curlFor(row.url, row.content_type || 'application/json')}
            </pre>
            <CopyButton text={curlFor(row.url, row.content_type || 'application/json')} />
          </div>
        </details>
      )}
    </div>
  );
};

// ── the editor ────────────────────────────────────────────────────────────────

const StubEditor: React.FC<{
  initial: Partial<MockEndpoint>;
  limits: MockEndpointLimits;
  onClose: () => void;
  onSaved: () => void;
}> = ({ initial, limits, onClose, onSaved }) => {
  const [form, setForm] = useState<Partial<MockEndpoint>>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<MockEndpoint>) => setForm(f => ({ ...f, ...patch }));

  const contentType = form.content_type || 'application/json';
  const body = form.body || '';
  const problem = useMemo(
    () => bodyProblem(body, contentType, limits.max_body_bytes),
    [body, contentType, limits.max_body_bytes],
  );
  const bytes = byteLength(body);

  const save = async () => {
    if (!(form.name || '').trim()) { setError('A name is needed.'); return; }
    if (problem?.level === 'error') { setError(problem.message); return; }
    setSaving(true); setError(null);
    const payload: Partial<MockEndpoint> = {
      name: form.name, description: form.description,
      status_code: Number(form.status_code) || 200,
      content_type: contentType, body,
      delay_ms: Number(form.delay_ms) || 0,
      enabled: form.enabled !== false,
      capture_requests: form.capture_requests !== false,
      expires_at: form.expires_at || null,
    };
    const res = form.id
      ? await mockEndpointsService.update(form.id, payload)
      : await mockEndpointsService.create(payload);
    setSaving(false);
    if (res.success) onSaved();
    else setError(res.error || 'Could not save that');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className={`${card} w-full max-w-2xl my-8 p-5`}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {form.id ? 'Edit stub' : 'New stub'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className={labelClass} htmlFor="stub-name">Name</label>
            <input id="stub-name" className={inputClass} value={form.name || ''}
                   onChange={e => set({ name: e.target.value })}
                   placeholder="Switch agent list — staging" />
          </div>

          <div>
            <label className={labelClass} htmlFor="stub-desc">
              Description <span style={{ color: 'var(--text-muted)' }}>— optional</span>
            </label>
            <input id="stub-desc" className={inputClass} value={form.description || ''}
                   onChange={e => set({ description: e.target.value })}
                   placeholder="What this stands in for, and for whom" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass} htmlFor="stub-status">Status code</label>
              <input id="stub-status" type="number" className={inputClass}
                     value={form.status_code ?? 200}
                     onChange={e => set({ status_code: Number(e.target.value) })} />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                Any 2xx–5xx. A failure status is often the point.
              </p>
            </div>
            <div>
              <label className={labelClass} htmlFor="stub-type">Content type</label>
              <select id="stub-type" className={inputClass} value={contentType}
                      onChange={e => set({ content_type: e.target.value })}>
                {limits.allowed_content_types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="stub-delay">Delay (ms)</label>
              <input id="stub-delay" type="number" className={inputClass}
                     value={form.delay_ms ?? 0}
                     onChange={e => set({ delay_ms: Number(e.target.value) })} />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                Up to {limits.max_delay_ms.toLocaleString()} — to exercise a caller's timeout.
              </p>
            </div>
          </div>

          {/* Said out loud rather than silently omitted from the list above. */}
          <p className="text-[11px] rounded-lg px-2 py-1.5 bg-gray-50 dark:bg-gray-900"
             style={{ color: 'var(--text-muted)' }}>
            HTML, SVG and script types are not offered: this serves your bytes from a cpass domain,
            and a browser would run them. Anything a caller parses as data is here.
          </p>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className={labelClass} htmlFor="stub-body">Response body</label>
              <div className="flex items-center gap-2">
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {humanBytes(bytes)} of {humanBytes(limits.max_body_bytes)}
                </span>
                {isJsonType(contentType) && (
                  <button type="button" onClick={() => set({ body: formatBody(body, contentType) })}
                    className="text-[11px] px-2 py-0.5 rounded-lg border border-gray-200
                               dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    Format
                  </button>
                )}
              </div>
            </div>
            <textarea id="stub-body" className={`${inputClass} font-mono text-xs`} rows={12}
                      value={body} onChange={e => set({ body: e.target.value })} />
            {problem && (
              <p className={`text-[11px] mt-1.5 rounded px-2 py-1.5 ${
                problem.level === 'error'
                  ? 'bg-rose-50 text-rose-800 dark:bg-rose-900/20 dark:text-rose-200'
                  : 'bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                {problem.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="stub-expires">
                Expires <span style={{ color: 'var(--text-muted)' }}>— optional</span>
              </label>
              <input id="stub-expires" type="datetime-local" className={inputClass}
                     value={(form.expires_at || '').slice(0, 16)}
                     onChange={e => set({ expires_at: e.target.value })} />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                After this it answers 410. Leave blank to keep it indefinitely.
              </p>
            </div>
            <div className="space-y-2 pt-5">
              <label className="flex items-center gap-2 text-sm">
                <input id="stub-enabled" type="checkbox" checked={form.enabled !== false}
                       onChange={e => set({ enabled: e.target.checked })} />
                <span className="text-gray-700 dark:text-gray-300">Answering</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input id="stub-capture" type="checkbox" checked={form.capture_requests !== false}
                       onChange={e => set({ capture_requests: e.target.checked })} />
                <span className="text-gray-700 dark:text-gray-300">Record what callers send</span>
              </label>
              <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Keeps the last {limits.keep_requests_per_endpoint} hits. Turn it off if callers post
                personal data you would rather cpass did not retain.
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-xl px-3 py-2 text-sm bg-rose-50 text-rose-800
                            dark:bg-rose-900/20 dark:text-rose-200">{error}</div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="text-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving}
            className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {form.id ? 'Save' : 'Create and get the URL'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── what callers actually sent ────────────────────────────────────────────────

const HitInspector: React.FC<{
  endpoint: MockEndpoint;
  limits: MockEndpointLimits;
  onClose: () => void;
}> = ({ endpoint, limits, onClose }) => {
  const [hits, setHits] = useState<MockEndpointHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await mockEndpointsService.hits(endpoint.id as number, 0, 50);
    setLoading(false);
    if (res.success) { setHits(res.data?.content || []); setError(null); }
    else setError(res.error || 'Could not load what callers sent');
  }, [endpoint.id]);

  useEffect(() => { load(); }, [load]);

  const clear = async () => {
    if (!window.confirm('Delete every recorded hit for this stub? This cannot be undone.')) return;
    const res = await mockEndpointsService.clearHits(endpoint.id as number);
    if (res.success) load();
    else setError(res.error || 'Could not clear the log');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className={`${card} w-full max-w-3xl my-8 p-5`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              Requests to {endpoint.name}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              The last {limits.keep_requests_per_endpoint} hits, newest first.
              {endpoint.capture_requests === false && ' Recording is currently off for this stub.'}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={load}
              className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                         hover:bg-gray-50 dark:hover:bg-gray-700" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={clear}
              className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                         hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600">
              Clear
            </button>
            <button type="button" onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-xl px-3 py-2 text-sm bg-rose-50 text-rose-800
                          dark:bg-rose-900/20 dark:text-rose-200">{error}</div>
        )}

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm py-8 justify-center"
                 style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : hits.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>
              Nothing has called this URL yet.
            </p>
          ) : hits.map(hit => (
            <div key={hit.id}
                 className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button type="button"
                onClick={() => setOpen(open === hit.id ? null : hit.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left
                           hover:bg-gray-50 dark:hover:bg-gray-700/50">
                {open === hit.id
                  ? <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100
                                 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  {hit.method}
                </span>
                <span className="text-xs truncate flex-1" style={{ color: 'var(--text-muted)' }}>
                  {hit.received_at ? new Date(hit.received_at).toLocaleString() : ''}
                  {hit.remote_ip ? ` · ${hit.remote_ip}` : ''}
                  {hit.body_bytes ? ` · ${humanBytes(hit.body_bytes)}` : ''}
                </span>
              </button>

              {open === hit.id && (
                <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                  {hit.query_string && (
                    <Field label="Query">
                      <code className="break-all">{hit.query_string}</code>
                    </Field>
                  )}
                  <Field label="Headers">
                    <div className="space-y-0.5">
                      {parseHeaders(hit.headers).map(([k, v]) => (
                        <div key={k} className="break-all">
                          <span className="text-gray-500 dark:text-gray-400">{k}: </span>
                          <span>{v}</span>
                        </div>
                      ))}
                    </div>
                  </Field>
                  <Field label="Body">
                    {/* Written by a stranger — rendered as text, never as markup. */}
                    <pre className="whitespace-pre-wrap break-all">{hit.body || '(empty)'}</pre>
                    {hit.body_truncated && (
                      <p className="mt-1 text-amber-700 dark:text-amber-300">
                        Truncated — {humanBytes(hit.body_bytes)} arrived and the first
                        {' '}{humanBytes(limits.max_captured_body_bytes)} were kept.
                      </p>
                    )}
                  </Field>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── time windows ──────────────────────────────────────────────────────────────

const DAY_OPTIONS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** A blank window, pre-filled with the shape most people want first. */
const newWindow = (): Partial<MockEndpointWindow> => ({
  name: '',
  priority: 100,
  start_time: '09:00',
  end_time: '20:00',
  days: '',
  zone_id: 'Asia/Kolkata',
  enabled: true,
  body: '',
});

/**
 * Editing when a stub answers with what.
 *
 * The stub this was built for tells a telephony vendor which team takes the
 * call, and that is not the same list at 3am as at 3pm. Three things the screen
 * has to say out loud, because each is silent when wrong — the call is simply
 * offered to the wrong team and nobody sees an error:
 *
 *   the ZONE, since the server runs in UTC and the business does not;
 *   that a window whose start is later than its end runs THROUGH midnight;
 *   that the first OPEN window wins, so order is the whole behaviour.
 */
const WindowEditor: React.FC<{
  endpoint: MockEndpoint;
  limits: MockEndpointLimits;
  onClose: () => void;
}> = ({ endpoint, limits, onClose }) => {
  const [rows, setRows] = useState<MockEndpointWindow[]>([]);
  const [status, setStatus] = useState<MockEndpointWindowStatus | null>(null);
  const [draft, setDraft] = useState<Partial<MockEndpointWindow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, now] = await Promise.all([
      mockEndpointsService.windows(endpoint.id as number),
      mockEndpointsService.windowStatus(endpoint.id as number),
    ]);
    setLoading(false);
    if (list.success) setRows(list.data || []);
    else setError(list.error || 'Could not load the time windows');
    if (now.success) setStatus(now.data || null);
  }, [endpoint.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    const res = draft.id
      ? await mockEndpointsService.updateWindow(endpoint.id as number, draft.id, draft)
      : await mockEndpointsService.createWindow(endpoint.id as number, draft);
    setBusy(false);
    if (res.success) { setDraft(null); load(); }
    else setError(res.error || 'Could not save that window');
  };

  const remove = async (w: MockEndpointWindow) => {
    if (!window.confirm(`Delete the "${w.name}" window?\n\nCallers in those hours will get the stub's default response instead.`)) return;
    setBusy(true);
    const res = await mockEndpointsService.removeWindow(endpoint.id as number, w.id as number);
    setBusy(false);
    if (res.success) load();
    else setError(res.error || 'Could not delete that window');
  };

  const toggleDay = (day: string) => {
    const current = (draft?.days || '').split(',').map((d) => d.trim()).filter(Boolean);
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setDraft({ ...draft, days: next.join(',') });
  };

  const draftCrossesMidnight = !!draft?.start_time && !!draft?.end_time
    && draft.start_time > draft.end_time;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className={`${card} w-full max-w-3xl my-8`}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              Time windows — {endpoint.name}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Answer differently by time of day. The first window that is open wins;
              when none is open the stub&rsquo;s own response is used.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* What is answering RIGHT NOW — so windows can be checked without waiting for a call. */}
        {status && (
          <div className="px-4 py-2.5 text-xs border-b border-gray-200 dark:border-gray-700
                          bg-gray-50 dark:bg-gray-900/40 flex items-center gap-2 flex-wrap">
            <Clock className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
            {status.serving === 'window' ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                Right now: <strong>{status.open_window}</strong> is answering.
              </span>
            ) : (
              <span style={{ color: 'var(--text-muted)' }}>
                Right now: no window is open, so the stub&rsquo;s default response is answering.
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="mx-4 mt-3 p-2.5 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-700
                          dark:text-rose-300 text-xs flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="p-4 space-y-2">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              No time windows. This stub answers the same way at every hour.
            </p>
          ) : rows.map((w) => (
            <div key={w.id} className="flex items-start gap-3 p-3 rounded-lg border
                                       border-gray-200 dark:border-gray-700 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900 dark:text-white">{w.name}</span>
                  <span className="text-[11px] font-mono px-1.5 py-0.5 rounded
                                   bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {w.start_time?.slice(0, 5)}–{w.end_time?.slice(0, 5)}
                  </span>
                  {/* Stated, not left to be inferred: this is the one that silently
                      never matches if somebody reads it as an empty range. */}
                  {w.crosses_midnight && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded
                                     bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                      <Moon className="w-3 h-3" /> overnight
                    </span>
                  )}
                  {!w.enabled && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700
                                     text-gray-500">off</span>
                  )}
                </div>
                <div className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {w.days ? w.days : 'every day'} · {w.zone_id} · checked {w.priority}
                  {status?.open_window === w.name && (
                    <span className="ml-2 text-emerald-600 dark:text-emerald-400">· open now</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={() => setDraft(w)}
                  className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                             hover:bg-gray-50 dark:hover:bg-gray-700">Edit</button>
                <button type="button" onClick={() => remove(w)} disabled={busy} title="Delete"
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600
                             hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}

          {!draft && (
            <button type="button" onClick={() => setDraft(newWindow())}
              className="mt-2 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border
                         border-dashed border-gray-300 dark:border-gray-600 w-full justify-center
                         hover:bg-gray-50 dark:hover:bg-gray-700">
              <Plus className="w-4 h-4" /> Add a time window
            </button>
          )}
        </div>

        {draft && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name">
                <input className={inputClass} value={draft.name || ''} placeholder="Reservation"
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </Field>
              <Field label="Checked (lower first)">
                <input type="number" className={inputClass} value={draft.priority ?? 100}
                  onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })} />
              </Field>
              <Field label="From (inclusive)">
                <input type="time" className={inputClass} value={(draft.start_time || '').slice(0, 5)}
                  onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} />
              </Field>
              <Field label="Until (exclusive)">
                <input type="time" className={inputClass} value={(draft.end_time || '').slice(0, 5)}
                  onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} />
              </Field>
            </div>

            {draftCrossesMidnight && (
              <p className="text-xs flex items-start gap-1.5 text-indigo-700 dark:text-indigo-300">
                <Moon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                This window runs through midnight — from {draft.start_time} tonight
                until {draft.end_time} the next morning.
              </p>
            )}

            <Field label="Days">
              <div className="flex gap-1.5 flex-wrap">
                {DAY_OPTIONS.map((d) => {
                  const on = (draft.days || '').split(',').map((x) => x.trim()).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleDay(d)}
                      className={`text-xs px-2 py-1 rounded-lg border capitalize ${
                        on ? 'bg-blue-600 text-white border-blue-600'
                           : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                None selected means every day.
              </p>
            </Field>

            <Field label="Time zone">
              <input className={inputClass} value={draft.zone_id || 'Asia/Kolkata'}
                onChange={(e) => setDraft({ ...draft, zone_id: e.target.value })} />
              {/* The mistake that shifts a working day by 5.5 hours and says nothing. */}
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                Hours are read in this zone, not the server&rsquo;s.
              </p>
            </Field>

            <Field label="Response body for these hours">
              <textarea className={`${inputClass} font-mono text-xs`} rows={8}
                value={draft.body || ''} placeholder={'{\n  "result": []\n}'}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                Leave empty to use the stub&rsquo;s own response for these hours.
                Limit {humanBytes(limits.max_body_bytes)}.
              </p>
            </Field>

            <div className="flex items-center gap-2">
              <button type="button" onClick={save} disabled={busy}
                className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {draft.id ? 'Save window' : 'Add window'}
              </button>
              <button type="button" onClick={() => { setDraft(null); setError(null); }}
                className="text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600
                           hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide mb-0.5"
         style={{ color: 'var(--text-muted)' }}>{label}</div>
    <div className="text-[11px] font-mono p-2 rounded-lg bg-gray-50 dark:bg-gray-900
                    border border-gray-200 dark:border-gray-700 max-h-64 overflow-auto">
      {children}
    </div>
  </div>
);

export default WebhookStubs;
